import React, { useState } from 'react';

const QuizResult = () => {
  const [quizId, setQuizId] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Search, 2: Details/Result
  const [loading, setLoading] = useState(false);

  // Simulated valid Quiz IDs
  const validIds = ['QZ-2024-001', 'QZ-2026-ABC'];

  const handleSearch = (e) => {
    e.preventDefault();
    setError('');
    
    if (!quizId.trim()) {
      setError('Please enter a valid Quiz ID');
      return;
    }

    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      if (validIds.includes(quizId.toUpperCase())) {
        setStep(2);
      } else {
        setError('Quiz ID not found. Please check and try again.');
      }
      setLoading(false);
    }, 1000);
  };

  return (
    <main className="pt-[140px] pb-20 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-5 text-center">
        
        <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
          <span className="bg-amber-50 dark:bg-amber-900/30 text-amber-600 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest border border-amber-100 dark:border-amber-800">
            Hall of Fame
          </span>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white mt-6 mb-4">
            Check Quiz <span className="text-primary">Results</span>
          </h1>
        </div>

        {/* Main Result Card */}
        <div className="max-w-3xl mx-auto bg-[#0a0f1d] rounded-[40px] shadow-2xl border border-slate-800 p-8 md:p-14 text-left relative overflow-hidden">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center border border-amber-500/20">
              <span className="text-2xl">🏆</span>
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight">Check Your Result</h2>
          </div>

          {step === 1 ? (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              <form onSubmit={handleSearch} className="mb-8">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4 ml-1">
                  Enter Quiz ID
                </label>
                <div className="relative group">
                  <input 
                    type="text" 
                    placeholder="e.g. QZ-2024-001"
                    value={quizId}
                    onChange={(e) => setQuizId(e.target.value)}
                    className={`w-full bg-[#111827] border-2 rounded-2xl py-5 pl-6 pr-16 outline-none transition-all font-bold text-white text-lg ${error ? 'border-red-500/50' : 'border-slate-800 focus:border-primary/50'}`}
                  />
                  <button 
                    type="submit"
                    disabled={loading}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-primary rounded-xl flex items-center justify-center hover:bg-primary/90 transition-all active:scale-90"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                    )}
                  </button>
                </div>
                {error && <p className="text-red-400 text-xs font-bold mt-4 ml-2 animate-pulse">⚠️ {error}</p>}
              </form>

              {/* Info Box - Matches screenshot */}
              <div className="bg-[#111827]/50 border border-slate-800 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center shrink-0 border border-blue-500/20">
                  <span className="text-blue-400">💡</span>
                </div>
                <div>
                  <h4 className="text-sm font-black text-slate-200 mb-1">Did you know?</h4>
                  <p className="text-[13px] text-slate-500 font-medium leading-relaxed">
                    Participating in quizzes earns you "Knowledge Points" which can be redeemed for premium course discounts.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Result Form Step - Matches user details request */
            <div className="animate-in zoom-in-95 duration-500">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-2 text-emerald-400 text-[10px] font-black uppercase tracking-widest inline-block mb-8">
                Quiz ID Verified: {quizId.toUpperCase()}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 ml-1">Full Name</label>
                  <input type="text" placeholder="Enter your registered name" className="w-full bg-[#111827] border border-slate-800 rounded-xl py-4 px-5 outline-none focus:border-primary text-white font-bold" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 ml-1">Email Address</label>
                  <input type="email" placeholder="akhil@example.com" className="w-full bg-[#111827] border border-slate-800 rounded-xl py-4 px-5 outline-none focus:border-primary text-white font-bold" />
                </div>
              </div>

              <button className="w-full py-5 bg-primary text-white rounded-2xl font-black text-lg transition-all shadow-lg active:scale-95 mb-6">
                View Scorecard 📊
              </button>
              
              <button 
                onClick={() => setStep(1)}
                className="w-full py-4 border border-slate-800 text-slate-500 rounded-2xl font-black text-sm hover:bg-slate-800/30 transition-all"
              >
                Back to Search
              </button>
            </div>
          )}
        </div>

        <p className="mt-12 text-sm text-slate-400 font-medium italic">
          * Results are usually published within 48 hours after the quiz completion.
        </p>
      </div>
    </main>
  );
};

export default QuizResult;